import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, useAuth } from "../App";
import { Heart, MapPin, Stethoscope, Home, HandHeart, X, Plus, ArrowRight, Activity } from "lucide-react";
import PayButton from "../components/PayButton";

const STATUS_META = {
  open:       { label: "Needs Help",   bg: "bg-brand text-white",     icon: HandHeart },
  at_clinic:  { label: "At Vet",       bg: "bg-brand-blue text-white", icon: Stethoscope },
  in_foster:  { label: "In Foster",    bg: "bg-leaf text-ink",         icon: Home },
  adopted:    { label: "Adopted",      bg: "bg-brand-green text-white",icon: Heart },
  deceased:   { label: "Rest In Peace",bg: "bg-ink text-white",        icon: Heart },
};

export default function RescueCases() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [activeCase, setActiveCase] = useState(null);
  const [filter, setFilter] = useState("");

  const load = () => axios.get(`${API}/rescue-cases${filter ? `?status=${filter}` : ""}`).then(r => setItems(r.data.items));
  useEffect(() => { load(); }, [filter]);

  return (
    <div className="space-y-6" data-testid="rescue-cases-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold">Rescue-to-Home</div>
          <h1 className="font-heading text-3xl font-extrabold mt-1">Pets seeking help</h1>
          <p className="text-ink-soft text-sm max-w-xl">Every rescue is a real animal with a story. Sponsor their journey from street → vet → foster → forever home. 100% of your support goes to verified NGOs.</p>
        </div>
        {user?.role === "ngo" && (
          <Link to="/app/ngo/rescue-cases" className="bg-ink text-white rounded-full px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5">
            <Plus className="w-4 h-4"/> Manage my cases
          </Link>
        )}
      </header>
      <div className="flex flex-wrap gap-2">
        {["", "open", "at_clinic", "in_foster", "adopted"].map(s => (
          <button key={s || "all"} data-testid={`filter-${s || "all"}`} onClick={() => setFilter(s)} className={`px-4 py-1.5 rounded-full text-xs font-bold border ${filter === s ? "bg-ink text-white border-ink" : "bg-surface border-line text-ink-soft hover:bg-surface-muted"}`}>
            {s ? STATUS_META[s].label : "All active"}
          </button>
        ))}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map(c => <RescueCard key={c.rescue_case_id} c={c} onOpen={() => setActiveCase(c)}/>)}
      </div>
      {items.length === 0 && (
        <div className="bg-surface border border-dashed border-line rounded-3xl p-10 text-center text-ink-soft">No active rescues right now — that's a beautiful thing. Check back soon.</div>
      )}
      {activeCase && <CaseDetailModal caseId={activeCase.rescue_case_id} onClose={() => { setActiveCase(null); load(); }}/>}
    </div>
  );
}

function RescueCard({ c, onOpen }) {
  const meta = STATUS_META[c.status] || STATUS_META.open;
  const Icon = meta.icon;
  const funded = c.donations_received || 0;
  const goal = c.estimated_treatment_cost || 0;
  const pct = goal > 0 ? Math.min(100, Math.round((funded / goal) * 100)) : (funded > 0 ? 100 : 0);
  return (
    <article data-testid={`rescue-${c.rescue_case_id}`} onClick={onOpen} className="cursor-pointer bg-surface border border-line rounded-3xl overflow-hidden shadow-soft hover:-translate-y-1 hover:shadow-hover transition-all">
      <div className="relative h-48">
        <img src={c.photo} alt={c.pet_name} className="w-full h-full object-cover"/>
        <div className={`absolute top-3 left-3 ${meta.bg} rounded-full px-3 py-1 text-[11px] font-bold inline-flex items-center gap-1`}>
          <Icon className="w-3 h-3"/> {meta.label}
        </div>
        <div className="absolute bottom-3 right-3 bg-black/60 text-white rounded-full px-3 py-1 text-[11px] font-bold">{c.ngo_name}</div>
      </div>
      <div className="p-5">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-heading text-xl font-extrabold">{c.pet_name}</h3>
          <span className="text-xs uppercase tracking-wider font-bold bg-surface-muted rounded-full px-2 py-1">{c.species}</span>
        </div>
        <div className="text-xs text-ink-soft uppercase tracking-wider font-bold mt-1">{c.breed} · {c.age_estimate}</div>
        <p className="text-sm text-ink-soft mt-2 line-clamp-2">{c.description}</p>
        <div className="mt-3 text-xs text-ink-soft inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{c.intake_location}</div>
        {(goal > 0 || funded > 0) && (
          <div className="mt-3">
            <div className="flex justify-between text-[11px] font-bold text-ink-soft mb-1">
              <span>Treatment funded</span>
              <span>₹{funded.toLocaleString()} {goal ? `/ ₹${goal.toLocaleString()}` : ""}</span>
            </div>
            <div className="h-2 rounded-full bg-surface-muted overflow-hidden">
              <div className="h-full bg-brand-green" style={{width: `${pct}%`}}/>
            </div>
          </div>
        )}
        <div className="mt-4 flex gap-2">
          <button className="flex-1 bg-brand text-white rounded-full py-2.5 text-sm font-bold hover:bg-brand-hover inline-flex items-center justify-center gap-1">
            <HandHeart className="w-4 h-4"/> Sponsor
          </button>
          <button className="px-3 rounded-full text-sm font-bold border border-line text-ink-soft hover:bg-surface-muted">
            <ArrowRight className="w-4 h-4"/>
          </button>
        </div>
      </div>
    </article>
  );
}

function CaseDetailModal({ caseId, onClose }) {
  const [c, setC] = useState(null);
  const [mode, setMode] = useState("view"); // view | sponsor | donate
  useEffect(() => { axios.get(`${API}/rescue-cases/${caseId}`).then(r => setC(r.data)); }, [caseId]);
  if (!c) return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-surface rounded-3xl p-6">Loading…</div>
    </div>
  );
  const meta = STATUS_META[c.status] || STATUS_META.open;
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl w-full max-w-2xl border border-line shadow-soft max-h-[90vh] overflow-y-auto" data-testid="case-detail-modal">
        <div className="relative h-56">
          <img src={c.photo} alt={c.pet_name} className="w-full h-full object-cover rounded-t-3xl"/>
          <button onClick={onClose} className="absolute top-3 right-3 bg-black/60 text-white p-2 rounded-full"><X className="w-4 h-4"/></button>
          <div className={`absolute top-3 left-3 ${meta.bg} rounded-full px-3 py-1 text-[11px] font-bold inline-flex items-center gap-1`}>{meta.label}</div>
        </div>
        <div className="p-6">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="font-heading text-2xl font-extrabold">{c.pet_name}</h2>
              <div className="text-xs text-ink-soft uppercase tracking-wider font-bold mt-1">{c.species} · {c.breed} · {c.age_estimate}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-ink-soft font-bold">Rescued by</div>
              <div className="font-heading font-extrabold">{c.ngo_name}</div>
            </div>
          </div>
          <p className="text-sm text-ink-soft mt-3">{c.description}</p>
          <div className="mt-4 grid grid-cols-3 gap-3">
            <Stat label="Sponsors" value={c.sponsors_count || 0}/>
            <Stat label="Raised" value={`₹${(c.donations_received || 0).toLocaleString()}`}/>
            <Stat label="Goal" value={c.estimated_treatment_cost ? `₹${c.estimated_treatment_cost.toLocaleString()}` : "—"}/>
          </div>
          {c.vet && (
            <div className="mt-4 bg-surface-muted rounded-2xl p-4 flex items-center gap-3">
              <Stethoscope className="w-5 h-5 text-brand-blue"/>
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-ink-soft">Pro-bono vet</div>
                <div className="font-bold">{c.vet.name} · {c.vet.clinic}</div>
              </div>
            </div>
          )}
          {c.foster && (
            <div className="mt-3 bg-leaf/50 rounded-2xl p-4 flex items-center gap-3">
              <Home className="w-5 h-5 text-ink"/>
              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-ink-soft">Foster home</div>
                <div className="font-bold">Currently with foster · {c.foster.city}</div>
              </div>
            </div>
          )}
          {c.timeline?.length > 0 && (
            <div className="mt-5">
              <div className="text-xs uppercase tracking-wider font-bold text-ink-soft mb-2 inline-flex items-center gap-1"><Activity className="w-3 h-3"/>Journey</div>
              <div className="space-y-2">
                {c.timeline.slice().reverse().slice(0, 6).map((t, i) => (
                  <div key={i} className="flex gap-3 text-sm">
                    <div className="w-1.5 h-1.5 mt-2 rounded-full bg-brand"/>
                    <div className="flex-1">
                      <div className="font-bold">{t.note}</div>
                      <div className="text-xs text-ink-soft">{new Date(t.at).toLocaleDateString("en-IN", {day:"numeric", month:"short"})} · {t.by}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {mode === "view" && (
            <div className="mt-6 grid grid-cols-2 gap-3">
              <button onClick={() => setMode("sponsor")} data-testid="case-sponsor-btn" className="bg-brand text-white rounded-full py-3 font-bold inline-flex items-center justify-center gap-1.5">
                <HandHeart className="w-4 h-4"/> Sponsor Monthly
              </button>
              <button onClick={() => setMode("donate")} data-testid="case-donate-btn" className="bg-ink text-white rounded-full py-3 font-bold inline-flex items-center justify-center gap-1.5">
                <Heart className="w-4 h-4"/> One-time Donate
              </button>
            </div>
          )}
          {mode === "sponsor" && <SponsorForm caseId={caseId} ngoId={c.ngo_id} onDone={() => onClose()}/>}
          {mode === "donate" && <DonateForm ngoId={c.ngo_id} onDone={() => onClose()}/>}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-surface-muted rounded-2xl p-3 text-center">
      <div className="font-heading text-xl font-extrabold">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-ink-soft font-bold">{label}</div>
    </div>
  );
}

function SponsorForm({ caseId, ngoId, onDone }) {
  const [amount, setAmount] = useState(500);
  const [months, setMonths] = useState(3);
  const [done, setDone] = useState(false);
  const total = amount * months;
  const submit = async () => {
    await axios.post(`${API}/sponsorships/start`, { rescue_case_id: caseId, monthly_amount: amount, months_committed: months });
    setDone(true);
    setTimeout(onDone, 2000);
  };
  if (done) return (
    <div className="mt-5 text-center py-4">
      <HandHeart className="w-12 h-12 text-brand mx-auto"/>
      <div className="font-heading text-xl font-extrabold mt-2">You're now a sponsor 💛</div>
      <p className="text-sm text-ink-soft">Thank you. We'll send you monthly updates on this pet.</p>
    </div>
  );
  return (
    <div className="mt-5 bg-surface-muted/50 rounded-2xl p-4">
      <div className="text-xs uppercase tracking-wider font-bold text-ink-soft mb-3">Sponsor monthly</div>
      <div className="grid grid-cols-4 gap-2">
        {[200, 500, 1000, 2500].map(a => (
          <button key={a} data-testid={`spons-amt-${a}`} onClick={() => setAmount(a)} className={`rounded-full py-2 text-xs font-bold border ${amount === a ? "bg-brand text-white border-brand" : "bg-surface border-line"}`}>₹{a}/mo</button>
        ))}
      </div>
      <div className="mt-3 flex items-center gap-2">
        <label className="text-xs font-bold text-ink-soft">For</label>
        <select data-testid="spons-months" value={months} onChange={e => setMonths(Number(e.target.value))} className="bg-surface border border-line rounded-full px-3 py-1.5 text-sm font-bold">
          {[1, 3, 6, 12].map(m => <option key={m} value={m}>{m} month{m>1?"s":""}</option>)}
        </select>
        <div className="ml-auto text-sm font-bold">Total: ₹{total.toLocaleString()}</div>
      </div>
      <div className="mt-4">
        <PayButton amount={total} currency="INR" country="IN" purpose="donation" referenceId={ngoId} label={`Pledge ₹${total.toLocaleString()}`} dataTestid="spons-confirm" onSuccess={submit}/>
      </div>
    </div>
  );
}

function DonateForm({ ngoId, onDone }) {
  const [amount, setAmount] = useState(500);
  return (
    <div className="mt-5 bg-surface-muted/50 rounded-2xl p-4">
      <div className="text-xs uppercase tracking-wider font-bold text-ink-soft mb-3">One-time donation · 100% to NGO</div>
      <div className="grid grid-cols-4 gap-2">
        {[200, 500, 1000, 2500].map(a => (
          <button key={a} onClick={() => setAmount(a)} className={`rounded-full py-2 text-xs font-bold border ${amount === a ? "bg-brand text-white border-brand" : "bg-surface border-line"}`}>₹{a}</button>
        ))}
      </div>
      <div className="mt-4">
        <PayButton amount={amount} currency="INR" country="IN" purpose="donation" referenceId={ngoId} label={`Donate ₹${amount}`} dataTestid="donate-confirm" onSuccess={onDone}/>
      </div>
    </div>
  );
}
